// ========================================
// Hook לניהול אימות משתמשים
// ========================================
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

// יצירת Context לשיתוף מצב האימות בכל האפליקציה
const AuthContext = createContext(null);

/**
 * Provider לעטיפת האפליקציה - מנהל את מצב ההתחברות
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // בדיקת מצב התחברות קיים בטעינה
        const checkSession = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                setUser(session?.user || null);
            } catch (err) {
                console.error('שגיאה בבדיקת הסשן:', err);
            } finally {
                setIsLoading(false);
            }
        };

        checkSession();

        // האזנה לשינויים במצב ההתחברות
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                setUser(session?.user || null);
            }
        );

        return () => subscription.unsubscribe();
    }, []);

    /**
     * התחברות עם אימייל וסיסמה
     */
    const login = async (email, password) => {
        setIsLoading(true);
        setError(null);

        try {
            const { data, error: authError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (authError) throw authError;
            return { success: true, user: data.user };
        } catch (err) {
            const message = err.message === 'Invalid login credentials'
                ? 'אימייל או סיסמה שגויים'
                : 'שגיאה בהתחברות';
            setError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * יציאה מהחשבון
     */
    const logout = async () => {
        setIsLoading(true);
        try {
            await supabase.auth.signOut();
            setUser(null);
        } catch (err) {
            console.error('שגיאה ביציאה:', err);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * הרשמה (יצירת משתמש חדש)
     */
    const signUp = async (email, password) => {
        setIsLoading(true);
        setError(null);

        try {
            const { data, error: authError } = await supabase.auth.signUp({
                email,
                password,
            });

            if (authError) throw authError;
            return { success: true, user: data.user };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    };

    const value = {
        user,
        isLoading,
        error,
        login,
        logout,
        signUp,
        isLoggedIn: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook לשימוש במצב האימות
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth חייב להיות בתוך AuthProvider');
    }
    return context;
};
